import { useEffect, useState } from "react";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import NodiantCalendar from "./calendar/NodiantCalendar";

const surfaceSx = {
    borderRadius: 2.5,
    borderColor: "divider",
    overflow: "hidden",
};

function DeliveryMethodRow({
    checked,
    description,
    disabled = false,
    divided = false,
    icon,
    name,
    onChange,
    title,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                borderTop: divided ? "1px solid" : "none",
                borderColor: "divider",
                opacity: disabled ? 0.62 : 1,
            }}
        >
            <Box sx={{ color: "text.secondary", display: "grid" }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Switch
                checked={checked}
                disabled={disabled}
                name={name}
                onChange={onChange}
                inputProps={{ "aria-label": title }}
            />
        </Box>
    );
}

export default function ShareDialogDeliveryOptions({
    assetType,
    embargoNotifications,
    fromDate,
    message,
    onDateChange,
    onDeliveryChange,
    onMessageChange,
    messageResetKey,
    sendEmails,
    sendNotifications,
}) {
    const deliveryEnabled = sendNotifications || sendEmails;
    const [draftMessage, setDraftMessage] = useState(message || "");

    useEffect(() => {
        setDraftMessage(message || "");
        // The reset key deliberately starts a fresh draft when a dialog opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messageResetKey]);

    const handleMessageChange = (event) => {
        const nextMessage = event.target.value;
        setDraftMessage(nextMessage);
        onMessageChange(nextMessage);
    };

    return (
        <Paper variant="outlined" sx={surfaceSx}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.25,
                    px: { xs: 1.5, sm: 2 },
                    py: 1.75,
                }}
            >
                <NotificationsOutlinedIcon
                    sx={{ color: "primary.main", mt: 0.25 }}
                />
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Notify recipients
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Choose how and when people are told about this {assetType}.
                    </Typography>
                </Box>
            </Box>
            <Divider />
            <DeliveryMethodRow
                icon={<NotificationsOutlinedIcon />}
                title="In-platform notification"
                description="Show an item in each recipient’s Nodiant feed."
                checked={sendNotifications}
                onChange={onDeliveryChange}
                name="notification"
            />
            <DeliveryMethodRow
                icon={<EmailOutlinedIcon />}
                title="Email"
                description="Email recipients who allow email notifications."
                checked={sendEmails}
                onChange={onDeliveryChange}
                name="email"
                divided
            />
            <DeliveryMethodRow
                icon={<CalendarMonthOutlinedIcon />}
                title="Notification embargo"
                description="Hold selected notifications and emails until a chosen date."
                checked={embargoNotifications}
                disabled={!deliveryEnabled}
                onChange={onDeliveryChange}
                name="embargo"
                divided
            />

            {deliveryEnabled && (
                <>
                    <Divider />
                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                        <TextField
                            label="Optional message"
                            value={draftMessage}
                            onChange={handleMessageChange}
                            multiline
                            minRows={3}
                            maxRows={8}
                            fullWidth
                        />
                    </Box>
                </>
            )}

            {embargoNotifications && (
                <>
                    <Divider />
                    <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800 }}
                        >
                            Embargo date
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.25, mb: 1.25 }}
                        >
                            Delivery is scheduled for 09:00 on the selected date.
                        </Typography>
                        <Box sx={{ maxWidth: 520, mx: "auto" }}>
                            <NodiantCalendar
                                selectedDate={fromDate}
                                onDateChange={onDateChange}
                                minDate={new Date()}
                            />
                        </Box>
                    </Box>
                </>
            )}
        </Paper>
    );
}
